import {
  AmountCellTwo,
  ApproverCell,
  CompanyWithInitialsCell,
  DebitBank,
  PayeeCell,
  RequestedDate,
  Rereference,
} from "@/app/components/common/table/KendoCustomCells";
import KendoTable, {
  type ColumnConfig,
} from "@/app/components/common/table/KendoTable";
import "../Dashboard.scss";
import {
  Checkbox,
  type CheckboxChangeEvent,
} from "@progress/kendo-react-inputs";
import { type ApprovalRequiredTransactions } from "@/app/types/payments";
import {
  type GridCellProps,
  type GridRowClickEvent,
} from "@progress/kendo-react-grid";
import { useT } from "@/app/hooks/useT";
import { useAppRoutingType } from "@/app/hooks/useAppRoutingType";
import { Constants } from "@/app/utils/AppConstants";

interface ApproveRequiredProp {
  onRowClick?: (event: GridRowClickEvent) => void;
  showAll?: boolean;
  tableList: ApprovalRequiredTransactions[];
  selectedTransactionList?: number[];
  setSelectedTransactionList?: React.Dispatch<React.SetStateAction<number[]>>;
  setCheckAll?: React.Dispatch<React.SetStateAction<boolean>>;
  checkAll?: boolean;
}

const CompanyCell = (props: GridCellProps) => (
  <CompanyWithInitialsCell approveRequired={false} {...props} />
);

const DebitBankCell = (props: GridCellProps) => <DebitBank {...props} />;

const BeneficiaryCell = (props: GridCellProps) => <PayeeCell {...props} />;

const DateCell = (props: GridCellProps) => <RequestedDate {...props} />;

const AmountCell = (props: GridCellProps) => <AmountCellTwo {...props} />;

const ReferenceCell = (props: GridCellProps) => <Rereference {...props} />;

const TotalApproverCell = (props: GridCellProps) => <ApproverCell {...props} />;

const CheckBoxCell = (
  props: GridCellProps,
  selectedTransactionList: number[],
  setSelectedTransaction?: React.Dispatch<React.SetStateAction<number[]>>,
  setCheckAll?: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  return (
    <td className="check-box-cell">
      <div className="flex items-center">
        <Checkbox
          className={
            props?.dataItem?.numberOfApproved ===
              props?.dataItem?.numberOfLevels - 1 ||
            props?.dataItem?.paymentStatusCode === 4447
              ? "cursor-not-allowed"
              : ""
          }
          checked={selectedTransactionList?.includes(props?.dataItem?.transId)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          disabled={
            props?.dataItem?.numberOfApproved ===
              props?.dataItem?.numberOfLevels - 1 ||
            props?.dataItem?.paymentStatusCode === 4447
          }
          onChange={() => {
            if (setSelectedTransaction) {
              if (selectedTransactionList?.includes(props?.dataItem?.transId)) {
                const filterTransaction = selectedTransactionList.filter(
                  (item: number) => item !== props?.dataItem?.transId,
                );
                setSelectedTransaction(filterTransaction);
              } else {
                setSelectedTransaction((prev: number[]) => [
                  ...prev,
                  props?.dataItem?.transId,
                ]);
              }
              if (setCheckAll) {
                setCheckAll(false);
              }
            }
          }}
        />
      </div>
    </td>
  );
};

const ApproveRequiredTable = ({
  onRowClick,
  showAll = false,
  tableList,
  selectedTransactionList = [],
  setSelectedTransactionList,
  setCheckAll,
  checkAll = false,
}: ApproveRequiredProp) => {
  const t = useT();
  const { appRoutingType } = useAppRoutingType();

  const columns: ColumnConfig[] =
    showAll === false || showAll === undefined
      ? [
          ...(appRoutingType === Constants.centralBank
            ? []
            : [
                {
                  field: "debitAccount",
                  title: t("debit_account"),
                  cell: CompanyCell,
                  // width: "25%",
                },
                {
                  field: "debitBank",
                  title: t("debit_bank"),
                  cell: DebitBankCell,
                  // width: "25%",
                },
              ]),
          {
            field: "beneNickname",
            title: t("beneficiary"),
            cell: CompanyCell,
            // width: appRoutingType === Constants.centralBank ? "22%" : "16%",
          },

          {
            field: "crtdDt",
            title: t("requested_date"),
            cell: DateCell,
            // width: appRoutingType === Constants.centralBank ? "22%" : "17%",
          },

          {
            field: "debitAmount",
            title: t("amount"),
            cell: AmountCell,
            // width: appRoutingType === Constants.centralBank ? "22%" : "16%",
            headerClassName: "amount-header-center",
          },

          {
            field: "gxChnNo",
            title: t("growx_reference"),
            cell: ReferenceCell,
            // width: appRoutingType === Constants.centralBank ? "22%" : "17%",
          },
          {
            field: "",
            title: "",
            cell: TotalApproverCell,
            width: 70,
          },
        ]
      : [
          {
            field: "",
            headerCell: (
              <Checkbox
                className={
                  tableList.every(
                    (item: ApprovalRequiredTransactions) =>
                      item?.numberOfApproved === item?.numberOfLevels - 1 ||
                      item?.paymentStatusCode === 4447,
                  )
                    ? "gx-font-medium cursor-not-allowed"
                    : "gx-font-medium"
                }
                checked={checkAll}
                disabled={tableList.every(
                  (item: ApprovalRequiredTransactions) =>
                    item?.numberOfApproved === item?.numberOfLevels - 1 ||
                    item?.paymentStatusCode === 4447,
                )}
                onChange={(event: CheckboxChangeEvent) => {
                  if (setCheckAll) {
                    setCheckAll(event?.value);
                    if (event?.value === false && setSelectedTransactionList) {
                      setSelectedTransactionList([]);
                    } else if (setSelectedTransactionList) {
                      const selected = tableList
                        ?.slice(0, 10)
                        ?.map((item: ApprovalRequiredTransactions) => {
                          return item?.transId;
                        });
                      setSelectedTransactionList(selected);
                    }
                  }
                }}
              />
            ),
            cell: (props) =>
              CheckBoxCell(
                props,
                selectedTransactionList,
                setSelectedTransactionList,
                setCheckAll,
              ),
            width: 60,
          },
          ...(appRoutingType === Constants.centralBank
            ? []
            : [
                {
                  field: "debitAccount",
                  title: t("debit_account"),
                  cell: CompanyCell,
                  // width: "25%",
                },
                {
                  field: "debitBank",
                  title: t("debit_bank"),
                  cell: DebitBankCell,
                  // width: "25%",
                },
              ]),
          {
            field: "accountNickname",
            title: t("beneficiary"),
            cell: BeneficiaryCell,
            // width: 150,
          },

          {
            field: "crtdDt",
            title: t("requested_date"),
            cell: DateCell,
            // width: 150,
          },

          {
            field: "debitAmount",
            title: t("amount"),
            cell: AmountCell,
            // width: 150,
          },
          {
            field: "gxChnNo",
            title: t("growx_reference"),
            cell: ReferenceCell,
            // width: 120,
          },
          {
            field: "",
            title: "",
            cell: TotalApproverCell,
            width: 70,
          },
        ];

  return (
    <KendoTable
      data={tableList}
      columns={columns}
      onRowClick={onRowClick}
      className="kendo-grid-transactions"
      // minTableWidth="900px"
      showHeader={tableList?.length > 0}
    />
  );
};

export default ApproveRequiredTable;
